import { CalendarDays, CalendarPlus, ListChecks, NotebookPen, Plus } from 'lucide-react';
import { CompactCalendar } from '../components/dashboard/CompactCalendar';
import { EmptyState } from '../components/dashboard/EmptyState';
import { SummaryCard } from '../components/dashboard/SummaryCard';
import { GhostIcon } from '../components/icons/SpookyIcons';
import type { ViewId } from '../components/layout/navigation';
import type { PlannerData, RecordKind } from '../types';
import { toDateKey } from './calendar-utils';

type Props = {
  data: PlannerData;
  onNavigate: (viewId: ViewId) => void;
  onNew: (kind: RecordKind) => void;
};

export const HomeView = ({ data, onNavigate, onNew }: Props) => {
  const greeting = new Intl.DateTimeFormat('en', { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());
  const today = toDateKey(new Date());
  const tasksToday = data.tasks.filter((task) => task.dueDate === today && task.status !== 'completed');
  const upcomingEvents = data.events.filter((event) => event.date >= today);
  const openShopping = data.shoppingItems.filter((item) => !item.completed);

  return (
    <div className="page-stack home-page">
      <section className="welcome-card" aria-labelledby="welcome-title">
        <img className="welcome-art" src="/assets/witch-cat.png" alt="" width="760" height="760" />
        <div>
          <p className="eyebrow">{greeting}</p>
          <h2 id="welcome-title">A quiet place for all your plans</h2>
          <p>Your clean planner is ready. Start with a task or choose a day for an event.</p>
          <div className="welcome-actions">
            <button type="button" className="primary-button" onClick={() => onNew('task')}><Plus size={18} aria-hidden="true" />New task</button>
            <button type="button" className="secondary-button" onClick={() => onNavigate('calendar')}><CalendarPlus size={18} aria-hidden="true" />Choose a date</button>
          </div>
        </div>
      </section>

      <section className="summary-grid" aria-label="Planner summary">
        <SummaryCard icon={ListChecks} label="Tasks today" value={tasksToday.length} onClick={() => onNavigate('tasks')} />
        <SummaryCard icon={CalendarDays} label="Upcoming events" value={upcomingEvents.length} onClick={() => onNavigate('calendar')} />
        <SummaryCard icon={NotebookPen} label="Notes" value={data.notes.length} onClick={() => onNavigate('notes')} />
        <SummaryCard icon={GhostIcon} label="Shopping items" value={openShopping.length} onClick={() => onNavigate('shopping')} />
      </section>

      <section className="dashboard-grid">
        <article className="dashboard-card plan-card">
          <div className="section-title-row">
            <div><p className="eyebrow">Today</p><h2>Your daily plan</h2></div>
            <button type="button" className="text-button" onClick={() => onNavigate('tasks')}>View tasks</button>
          </div>
          {tasksToday.length === 0 && upcomingEvents.filter((event) => event.date === today).length === 0 ? <EmptyState actionLabel="Plan a task" description="Tasks and events for today will gather here without clutter." onAction={() => onNew('task')} title="Nothing is haunting your schedule" /> : <ul className="today-list">{tasksToday.map((task) => <li key={task.id}><span>Task</span><strong>{task.title}</strong></li>)}{upcomingEvents.filter((event) => event.date === today).map((event) => <li key={event.id}><span>Event</span><strong>{event.title}</strong></li>)}</ul>}
        </article>
        <CompactCalendar onOpenCalendar={() => onNavigate('calendar')} />
      </section>
    </div>
  );
};
